import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';

export default function MyAttendance() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');

  const load = async () => {
    try {
      const res = await api.get('/staff-attendance/my-records');
      setRecords(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Build month options from records
  const months = useMemo(() => {
    const set = new Set();
    records.forEach(r => { if (r.date) set.add(r.date.substring(0, 7)); });
    return [...set].sort().reverse();
  }, [records]);

  // Filter records
  const filtered = useMemo(() => {
    let list = records;
    if (statusFilter !== 'all') list = list.filter(r => r.status === statusFilter);
    if (monthFilter !== 'all') list = list.filter(r => r.date && r.date.startsWith(monthFilter));
    return list;
  }, [records, statusFilter, monthFilter]);

  // Summary stats (based on filtered set)
  const stats = useMemo(() => {
    const total = filtered.length;
    const present = filtered.filter(r => r.status === 'Present').length;
    const absent = filtered.filter(r => r.status === 'Absent').length;
    const late = filtered.filter(r => r.status === 'Late').length;
    const halfDay = filtered.filter(r => r.status === 'Half-Day').length;
    const rate = total ? Math.round((present / total) * 100) : 0;
    return { total, present, absent, late, halfDay, rate };
  }, [filtered]);

  const monthLabel = (m) => {
    const d = new Date(m + '-01');
    return d.toLocaleDateString('en', { month: 'long', year: 'numeric' });
  };

  const fmtTime = (row, key) => row[`${key}_local`] || '—';

  if (loading) {
    return <div className="bg-white p-8 rounded shadow text-center text-gray-500">Loading…</div>;
  }

  return (
    <div>
      {/* ═══ Header ═══ */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <i className="fas fa-user-clock text-purple-500"></i>
            My Attendance
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Your personal attendance record. Only visible to you.
          </p>
        </div>
        <button
          onClick={() => { setRefreshing(true); load(); }}
          disabled={refreshing}
          className="bg-purple-100 text-purple-700 px-4 py-2 rounded hover:bg-purple-200 text-sm disabled:opacity-50"
        >
          <i className="fas fa-sync-alt mr-1"></i> Refresh
        </button>
      </div>

      {/* ═══ Summary Cards ═══ */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <StatCard label="Total Days" value={stats.total} color="gray" />
        <StatCard label="Present" value={stats.present} color="green" />
        <StatCard label="Absent" value={stats.absent} color="red" />
        <StatCard label="Late" value={stats.late} color="yellow" />
        <StatCard label="Attendance Rate" value={`${stats.rate}%`} color="blue" />
      </div>

      {/* ═══ Filters ═══ */}
      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-center">
        <select
          className="p-2 border rounded text-sm"
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
        >
          <option value="all">All Months</option>
          {months.map(m => (
            <option key={m} value={m}>{monthLabel(m)}</option>
          ))}
        </select>

        <select
          className="p-2 border rounded text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="Present">Present</option>
          <option value="Absent">Absent</option>
          <option value="Late">Late</option>
          <option value="Half-Day">Half-Day</option>
          <option value="Leave">Leave</option>
        </select>

        {(monthFilter !== 'all' || statusFilter !== 'all') && (
          <button
            onClick={() => { setMonthFilter('all'); setStatusFilter('all'); }}
            className="text-xs text-purple-600 hover:underline font-medium"
          >
            Clear filters
          </button>
        )}

        <span className="ml-auto text-sm text-gray-500">
          {filtered.length} record{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ═══ Table ═══ */}
      <div className="bg-white rounded shadow overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <i className="fas fa-calendar-times text-4xl mb-3"></i>
            <p>No attendance records for the selected filters.</p>
            {records.length === 0 && (
              <p className="text-xs mt-2">
                No records have been added for your account yet.
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Date', 'Day', 'Check-In', 'Check-Out', 'Hours', 'Status', 'Source'].map(h => (
                    <th key={h} className="p-3 text-left font-semibold text-gray-700 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const dayName = r.date
                    ? new Date(r.date).toLocaleDateString('en', { weekday: 'long' })
                    : '—';
                  const isWeekend = r.date && [0, 6].includes(new Date(r.date).getDay());
                  return (
                    <tr key={r.id} className="border-t hover:bg-gray-50">
                      <td className="p-3 font-mono text-xs text-gray-700">{r.date || '—'}</td>
                      <td className={`p-3 text-xs ${isWeekend ? 'text-gray-400' : 'text-gray-600'}`}>
                        {dayName}
                      </td>
                      <td className="p-3 font-mono text-xs">{fmtTime(r, 'check_in')}</td>
                      <td className="p-3 font-mono text-xs">{fmtTime(r, 'check_out')}</td>
                      <td className="p-3">
                        {r.worked_hours != null ? `${r.worked_hours}h` : '—'}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs rounded font-semibold ${
                          r.status === 'Present' ? 'bg-green-100 text-green-800'
                          : r.status === 'Absent' ? 'bg-red-100 text-red-800'
                          : r.status === 'Late' ? 'bg-yellow-100 text-yellow-800'
                          : r.status === 'Half-Day' ? 'bg-orange-100 text-orange-800'
                          : r.status === 'Leave' ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-600'
                        }`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="p-3 text-xs text-gray-500">{r.source || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ═══ Footer Note ═══ */}
      <div className="mt-4 text-xs text-gray-400 flex items-center gap-2">
        <i className="fas fa-info-circle"></i>
        <span>
          If a record is missing or incorrect, contact your administrator.
        </span>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  const colors = {
    gray:   { bg: 'bg-gray-50',   border: 'border-gray-400',   text: 'text-gray-700' },
    green:  { bg: 'bg-green-50',  border: 'border-green-500',  text: 'text-green-700' },
    red:    { bg: 'bg-red-50',    border: 'border-red-500',    text: 'text-red-700' },
    yellow: { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-700' },
    blue:   { bg: 'bg-blue-50',   border: 'border-blue-500',   text: 'text-blue-700' },
  };
  const c = colors[color] || colors.gray;
  return (
    <div className={`${c.bg} p-3 rounded shadow border-l-4 ${c.border}`}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-2xl font-bold ${c.text}`}>{value}</p>
    </div>
  );
}